/**
 * installWrap — the installer's Wrap Up and field payments (FIELD-PAY-01 +
 * INSTALLER-APP-01b; Seth approved the mock 2026-09-24, "Yes proceed").
 *
 * Top to bottom on the Wrap Up step of an INSTALL visit:
 *   Ready to Finish · After Photos (the list lives in Settings) · Customer
 *   Sign-Off · Balance → Collect
 * The notes and the Install Complete button stay in jobVisit's WrapStep;
 * useInstallReadiness tells it what's still missing — it asks why and tells
 * the office, it never locks the crew out.
 *
 * The Balance card follows the sale: paid in full, financing waiting on the
 * lender ("don't collect"), financed, billed after the install, or a balance
 * to collect. Collect = Card on File · Type In a Card · Check · Cash · Klarna
 * (the only QR code). Tap to Pay and the card reader arrive with the native
 * app update. Nothing here shows our cost; the office still builds the final
 * invoice and everything collected is applied to it at close.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Banknote, Camera, Check, CheckCircle2, ChevronRight, Clock, Copy, CreditCard, ExternalLink,
  FileText, Loader2, MapPin, Nfc, PenLine, QrCode, Smartphone, Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { haptic } from "@/native/boot";
import { InfoTip } from "@/components/InfoTip";
import { useJobEquipment, type Job } from "@/features/jobs/jobs";
import { useJobMoney, useRemovedEquipment, type JobMoney } from "@/features/jobs/installField";
import {
  useJobPayments, useFieldRecord, useFieldChargeSaved, useFieldChargeNewCard, settleFieldPayment, useFieldPayLink,
  useSaveSignoff, useAfterPhotoList, afterCaption, useFieldCashSettings, useFieldCash, useCashDroppedOff,
  methodLabel, COUNTED, type JobPayment, type PayLink,
} from "@/features/jobs/fieldPay";
import { uploadOneFile, useJobFiles, useSignedUrls, type PegasusFile } from "@/features/files/filesCore";
import { usePaymentConfig, useCustomerPaymentProfile, loadStripeJs, type StripeCardElement, type StripeJs } from "@/features/payments/payments";
import { SignaturePad } from "./SignaturePad";
import { Sheet, card, input } from "./installWork";
import { useRefrigerantKeys, useJobRefrigerant, refrigerantStatus, ozText } from "@/features/jobs/refrigerant";
import { useInstallCheckItems, useJobChecks, checklistFor, checklistProgress, autoAnswers } from "@/features/jobs/installChecklist";
import { useRebateTestStatus, useRebateTest, rebateCounts } from "@/features/jobs/rebateTest";

const money = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const clock = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
const firstOf = (name: string | null | undefined) => {
  const n = (name ?? "").trim();
  if (!n) return "The customer";
  // ESC names read "Tester, Mark" — the first name is after the comma.
  if (n.includes(",")) return n.split(",")[1]?.trim().split(/\s+/)[0] || n;
  return n.split(/\s+/)[0];
};

/* ------------------------------------------------------------ readiness */

export interface Readiness {
  units: number; unitsDone: number; before: number; oldUnits: number;
  after: { label: string; required: boolean; photos: PegasusFile[] }[];
  reqAfter: number; reqAfterDone: number;
  signed: boolean;
  money: JobMoney | null | undefined;
  /** REFRIG-01: only when the job has equipment that holds refrigerant. */
  refrig: { applies: boolean; done: boolean; addedOz: number; recoveredOz: number };
  /** INSTALL-CHECK-01: the Install Order lines that fit this job. */
  checklist: { total: number; answered: number; no: number };
  /** REBATE-TEST-01: only on a cooling-rebate job. */
  rebate: { needed: boolean; done: boolean; filled: number; total: number };
  /** Plain words for anything still missing — the Install Complete check asks why. */
  missing: string[];
}

export function useInstallReadiness(job: Job): Readiness {
  const { data: equip } = useJobEquipment(job.id);
  const { data: files } = useJobFiles(job.id);
  const { data: removed } = useRemovedEquipment(job.id);
  const { data: list } = useAfterPhotoList();
  const { data: m } = useJobMoney(job.id);
  const { data: refUses } = useRefrigerantKeys();
  const { data: refLogs } = useJobRefrigerant(job.id);
  const { data: checkItems } = useInstallCheckItems();
  const { data: answers } = useJobChecks(job.id);
  const { data: rbStatus } = useRebateTestStatus(job.id);
  const { data: rbRow } = useRebateTest(job.id);
  return useMemo(() => {
    const units = (equip ?? []).filter((e) => e.kind !== "accessory" && e.kind !== "material");
    const unitsDone = units.filter((u) => u.install_confirmed_at).length;
    const all = files ?? [];
    const before = all.filter((f) => /^before/i.test(f.caption ?? "")).length;
    const after = (list ?? []).filter((a) => a?.label?.trim()).map((a) => ({
      label: a.label.trim(), required: !!a.required, photos: all.filter((f) => (f.caption ?? "") === afterCaption(a.label.trim())),
    }));
    const req = after.filter((a) => a.required);
    const reqDone = req.filter((a) => a.photos.length > 0).length;
    const signed = !!job.signoff_at;
    const missing: string[] = [];
    if (units.length && unitsDone < units.length) missing.push(`${units.length - unitsDone} of ${units.length} unit${units.length === 1 ? "" : "s"} not confirmed`);
    if (!before) missing.push("Before photos");
    for (const a of req) if (!a.photos.length) missing.push(`Photo: ${a.label}`);
    const rs = refrigerantStatus(equip ?? [], removed ?? [], refLogs ?? [], refUses);
    for (const u of rs.chargeMissing) missing.push(`Refrigerant charge not logged: ${u.category || "new unit"}`);
    for (const r of rs.recoverMissing) missing.push(`Refrigerant recovered not logged: old ${r.category || "unit"}`);
    const lines = checklistFor(checkItems ?? [], (equip ?? []).filter((e) => e.kind !== "material"));
    const prog = checklistProgress(lines, answers, autoAnswers(refLogs ?? []));
    if (prog.missing.length) missing.push(`Install checklist: ${prog.missing.length} line${prog.missing.length === 1 ? "" : "s"} not answered`);
    for (const l of prog.no) missing.push(`Checklist says No: ${l.label}${answers?.get(l.key)?.note ? ` (${answers.get(l.key)!.note})` : ""}`);
    const rbNeeded = !!rbStatus?.needed;
    const rbDone = !!rbRow?.completed_at;
    const rbc = rebateCounts(rbRow?.data ?? {});
    if (rbNeeded && !rbDone) missing.push("Rebate test form");
    if (!signed) missing.push("Customer sign-off");
    if (m && m.collect === "ok" && m.due > 0.005) missing.push(`Balance ${money(m.due)} not collected`);
    return {
      units: units.length, unitsDone, before, oldUnits: (removed ?? []).length, after, reqAfter: req.length, reqAfterDone: reqDone, signed, money: m,
      refrig: { applies: rs.applies, done: rs.done, addedOz: rs.addedOz, recoveredOz: rs.recoveredOz },
      checklist: { total: prog.total, answered: prog.answered, no: prog.no.length },
      rebate: { needed: rbNeeded, done: rbDone, filled: rbc.done, total: rbc.total },
      missing,
    };
  }, [equip, files, removed, list, m, job.signoff_at, refUses, refLogs, checkItems, answers, rbStatus, rbRow]);
}

function Tick({ ok }: { ok: boolean }) {
  return (
    <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full", ok ? "bg-emerald-600 text-white" : "border-2 border-slate-300 bg-white")}>
      {ok && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
    </span>
  );
}

/** What finishes an install, checked off as it happens (refrigerant, the
 *  checklist and the rebate test only when the job has them). */
export function ReadyToFinishCard({ r }: { r: Readiness }) {
  const m = r.money;
  const balanceText = !m ? "Balance"
    : m.collect === "paid" ? "Balance collected"
    : m.collect === "financing_pending" ? "Financing — nothing to collect yet"
    : m.collect === "bill_after" ? "The office bills after the install"
    : m.collect === "closed" ? "Job closed by the office"
    : `Balance ${money(m.due)}`;
  const balanceOk = !!m && m.collect !== "ok";
  const row = (ok: boolean, text: string, right?: React.ReactNode) => (
    <li className="flex items-center gap-2.5 py-1.5">
      <Tick ok={ok} />
      <span className="min-w-0 flex-1 text-[14px] font-semibold text-slate-800">{text}</span>
      {right}
    </li>
  );
  return (
    <section className={card}>
      <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Ready to Finish</h4>
      <ul className="mt-1.5">
        {r.units > 0 && row(r.unitsDone === r.units, `${r.unitsDone} of ${r.units} unit${r.units === 1 ? "" : "s"} confirmed`)}
        {row(r.before > 0, "Before photos")}
        {r.reqAfter > 0 && row(r.reqAfterDone === r.reqAfter, "After photos",
          r.reqAfterDone < r.reqAfter ? <span className="m-chip">{r.reqAfterDone} of {r.reqAfter}</span> : null)}
        {r.refrig.applies && row(r.refrig.done, "Refrigerant logged",
          r.refrig.addedOz > 0 || r.refrig.recoveredOz > 0
            ? <span className="text-[11.5px] font-semibold text-slate-500">{[r.refrig.addedOz > 0 ? `${ozText(r.refrig.addedOz)} in` : null, r.refrig.recoveredOz > 0 ? `${ozText(r.refrig.recoveredOz)} out` : null].filter(Boolean).join(" · ")}</span>
            : null)}
        {r.checklist.total > 0 && row(r.checklist.answered === r.checklist.total && r.checklist.no === 0, "Install checklist",
          r.checklist.no > 0 ? <span className="m-chip !bg-amber-50 !text-amber-800">{r.checklist.no} No</span>
            : r.checklist.answered < r.checklist.total ? <span className="m-chip">{r.checklist.answered} of {r.checklist.total}</span> : null)}
        {r.rebate.needed && row(r.rebate.done, "Rebate test form",
          !r.rebate.done ? <span className="m-chip">{r.rebate.filled} of {r.rebate.total}</span> : null)}
        {row(r.signed, "Customer sign-off")}
        {row(balanceOk, balanceText)}
      </ul>
    </section>
  );
}

/* ---------------------------------------------------------- after photos */

export function AfterPhotosCard({ job, r }: { job: Job; r: Readiness }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const target = useRef<string>("");
  const shown = r.after.flatMap((a) => a.photos.slice(0, 1));
  const { data: urls } = useSignedUrls(shown);

  const pick = (label: string) => { target.current = label; fileRef.current?.click(); };
  const onFiles = async (list: FileList | null) => {
    const label = target.current;
    if (!list?.length || !label) return;
    setBusy(label);
    setErr(null);
    try {
      for (const f of Array.from(list)) {
        await uploadOneFile({ custno: job.custno, locno: job.locno, job_id: job.id, caption: afterCaption(label), doc_tag: "photo", origin: "tech" }, f);
      }
      haptic("success");
      void qc.invalidateQueries({ queryKey: ["files"] });
    } catch {
      setErr("A photo didn't upload — check the signal and try again.");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  if (r.after.length === 0) return null;
  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <Camera className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">After Photos</h4>
        <InfoTip label="Where this list comes from">The office sets this list in Settings → Field Payments &amp; Photos. Photos go on the job for the office, the warranty and the customer&apos;s file.</InfoTip>
        {r.reqAfter > 0 && (
          <span className={cn("m-chip ml-auto", r.reqAfterDone === r.reqAfter && "m-chip-good")}>{r.reqAfterDone} of {r.reqAfter}</span>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} />
      <ul className="mt-2 divide-y divide-slate-100">
        {r.after.map((a) => {
          const first = a.photos[0];
          const url = first ? urls?.get(first.storage_path) : null;
          return (
            <li key={a.label} className="flex items-center gap-3 py-2">
              <button type="button" onClick={() => pick(a.label)} disabled={busy === a.label}
                aria-label={first ? `Add another photo: ${a.label}` : `Take a photo: ${a.label}`}
                className={cn("flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl [touch-action:manipulation]",
                  first ? "bg-slate-200" : "border-2 border-dashed border-sky-300 bg-sky-50 text-sky-700")}>
                {busy === a.label ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                  : url ? <img src={url} alt="" className="h-full w-full object-cover" />
                  : first ? <Check className="h-5 w-5 text-emerald-600" /> : <Camera className="h-5 w-5" />}
              </button>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-slate-800">{a.label}</span>
                {a.photos.length > 1 && <span className="block text-[11.5px] text-slate-500">{a.photos.length} photos</span>}
              </span>
              {a.photos.length ? <Check className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={3} />
                : a.required ? <span className="m-chip shrink-0 !bg-amber-50 !text-amber-800">Needed</span>
                : <span className="shrink-0 text-[11.5px] font-semibold text-slate-400">Optional</span>}
            </li>
          );
        })}
      </ul>
      {err && <p className="mt-1 text-center text-[12px] font-semibold text-rose-600">{err}</p>}
    </section>
  );
}

/* ------------------------------------------------------------- sign-off */

export function SignOffCard({ job }: { job: Job }) {
  const [open, setOpen] = useState(false);
  const save = useSaveSignoff(job.id);
  const who = job.customer_name ?? "the customer";
  const first = firstOf(job.customer_name);
  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <PenLine className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Customer Sign-Off</h4>
        <InfoTip label="What to show them first">Walk {first} through the new system, the thermostat and the filter. Then they sign that the install is done and they were shown how it works. The signature goes on the final invoice.</InfoTip>
      </div>
      {job.signoff_at ? (
        <div className="mt-2.5 flex items-center gap-3 rounded-2xl bg-emerald-50/70 px-3 py-2.5">
          {job.signoff_signature && <img src={job.signoff_signature} alt="Customer signature" className="h-10 w-24 shrink-0 rounded-lg bg-white object-contain ring-1 ring-emerald-100" />}
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-emerald-800">
            Signed by {job.signoff_name ?? who} · {clock(job.signoff_at)}
          </span>
          <button type="button" onClick={() => setOpen(true)} className="shrink-0 text-[12.5px] font-bold text-emerald-700">Sign Again</button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="m-btn m-btn-tint m-btn-big mt-3 w-full">
          <PenLine className="h-[18px] w-[18px]" /> Customer Signs Off
        </button>
      )}
      {save.isError && <p className="mt-2 text-center text-[12px] font-semibold text-rose-600">{(save.error as Error).message}</p>}
      {open && (
        <SignaturePad
          customerName={who}
          title="Walkthrough Sign-Off"
          subtitle={[who, job.address].filter(Boolean).join(" · ")}
          consent={`By signing, ${first} confirms the install is complete and the system operation was explained and they understand it.`}
          doneLabel="Done — Signed"
          onClose={() => setOpen(false)}
          onDone={(dataUrl) => save.mutate({ name: job.customer_name ?? null, signature: dataUrl }, {
            onSuccess: () => { haptic("success"); setOpen(false); },
          })}
        />
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- balance */

/** The balance, following the sale. `collectable` adds the Collect button + the
 *  payments taken today (Wrap Up); Scope shows the same card read-only. */
export function BalanceCard({ job, collectable }: { job: Job; collectable: boolean }) {
  const { data: m, isLoading } = useJobMoney(job.id);
  const { data: pays } = useJobPayments(collectable ? job.id : null);
  const [open, setOpen] = useState(false);
  if (isLoading) return <div className="h-28 animate-pulse rounded-3xl bg-slate-200/60" />;
  if (!m) return null;
  if (m.contract <= 0 && m.total <= 0) {
    return (
      <section className={card}>
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Wallet className="h-4 w-4 text-slate-400" /> Balance</h4>
        <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-[13px] text-slate-500">No contract price on the job yet.</p>
      </section>
    );
  }
  const other = Math.round((m.changes - m.field_changes + m.custom) * 100) / 100;
  const depositOnly = m.deposit_billed > 0 && m.held <= 0 && m.paid <= m.deposit_billed + 0.005;
  const line = (label: string, value: string, tone?: string) => (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className={cn("text-[14px] font-semibold tabular-nums text-slate-800", tone)}>{value}</span>
    </div>
  );
  const financedPlan = m.fin_plan_name ?? "the financing plan";
  const today = (pays ?? []).filter((p) => p.held && (p.source === "field" || p.source === "lender") && (COUNTED.has(p.status) || p.status === "pending"));
  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Balance</h4>
        {collectable && <InfoTip label="Where this payment goes">The office still checks the final invoice. Whatever you collect is applied to it when they close the job.</InfoTip>}
      </div>
      <div className="mt-1.5">
        {line("Contract", money(m.contract))}
        {m.field_changes !== 0 && line(`Add-on${m.field_changes_n > 1 ? "s" : ""} sold today`, `+ ${money(m.field_changes)}`)}
        {other !== 0 && line("Other changes", `${other > 0 ? "+" : "−"} ${money(Math.abs(other))}`)}
        {m.paid > 0 && line(depositOnly ? "Paid at the sale" : "Paid so far", `− ${money(m.paid)}`, "text-emerald-700")}
        {m.financed > 0 && line(`Financed · ${financedPlan}`, `− ${money(m.financed)}`, "text-emerald-700")}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-slate-100 pt-2">
        <span className="text-[13.5px] font-bold text-slate-800">Balance</span>
        <span className="text-[22px] font-extrabold tabular-nums tracking-tight text-slate-900">{money(m.due)}</span>
      </div>

      {m.collect === "paid" && (
        <p className="mt-2.5 flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2.5 text-[13px] font-semibold text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Paid in full — nothing to collect.
        </p>
      )}
      {m.collect === "financing_pending" && (
        <div className="mt-2.5 rounded-2xl bg-amber-50 px-3 py-2.5">
          <p className="flex items-center gap-2 text-[13px] font-bold text-amber-900"><Clock className="h-4 w-4 shrink-0" /> Financing not approved yet — don&apos;t collect.</p>
          <p className="mt-1 text-[12.5px] leading-snug text-amber-900/80">The office is waiting on Wells Fargo to approve {financedPlan}. If {firstOf(job.customer_name)} wants to pay another way, call the office first.</p>
        </div>
      )}
      {m.pay_plan === "financing" && (m.fin_status === "approved" || m.fin_status === "funded") && m.collect !== "financing_pending" && (
        <p className="mt-2.5 rounded-2xl bg-slate-50 px-3 py-2.5 text-[13px] font-semibold text-slate-700">
          Financed — {financedPlan}{m.fin_amount ? ` · Wells Fargo approved ${money(m.fin_amount)}` : ""}
        </p>
      )}
      {m.collect === "bill_after" && (
        <p className="mt-2.5 rounded-2xl bg-slate-50 px-3 py-2.5 text-[13px] font-semibold text-slate-700">The office bills after the install.</p>
      )}
      {m.collect === "closed" && (
        <p className="mt-2.5 rounded-2xl bg-slate-50 px-3 py-2.5 text-[13px] font-semibold text-slate-700">The office has closed this job.</p>
      )}

      {collectable && today.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {today.map((p) => <PaymentRow key={p.id} p={p} />)}
        </ul>
      )}

      {collectable && (m.collect === "ok" || m.collect === "bill_after") && m.due > 0.005 && (
        <button type="button" onClick={() => setOpen(true)}
          className={cn("m-btn m-btn-big mt-3 w-full", m.collect === "ok" ? "m-btn-primary" : "m-btn-soft")}>
          <Wallet className="h-[18px] w-[18px]" /> {m.collect === "ok" ? `Collect ${money(m.due)}` : "They Want to Pay Now"}
        </button>
      )}
      {open && <CollectSheet job={job} money={m} onClose={() => setOpen(false)} />}
    </section>
  );
}

function PaymentRow({ p }: { p: JobPayment }) {
  const pending = p.status === "pending";
  return (
    <li className={cn("flex items-center gap-2 rounded-2xl px-3 py-2 text-[13px]", pending ? "bg-amber-50 text-amber-900" : "bg-emerald-50/70 text-emerald-900")}>
      {pending ? <Clock className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1 font-semibold">
        {pending ? `Waiting on ${methodLabel(p)}` : p.source === "lender" ? "Lender funded" : "Collected"} {money(p.amount)}
        <span className="font-medium opacity-75"> · {methodLabel(p)} · {clock(p.created_at)}</span>
      </span>
    </li>
  );
}

/* ---------------------------------------------------------------- collect */

type View = "menu" | "saved" | "card" | "check" | "cash" | "klarna" | "done";

function CollectSheet({ job, money: m, onClose }: { job: Job; money: JobMoney; onClose: () => void }) {
  const [view, setView] = useState<View>("menu");
  const [amountText, setAmountText] = useState(m.due.toFixed(2));
  const [editAmount, setEditAmount] = useState(false);
  const [done, setDone] = useState<{ amount: number; how: string; cash?: boolean } | null>(null);
  const { data: cfg } = usePaymentConfig();
  const { data: profile } = useCustomerPaymentProfile(job.custno);
  const cashCfg = useFieldCashSettings();
  // Klarna: the 24-hour link is made on the TAP (an effect-started mutation is
  // orphaned by React's double mount in development — the QR never showed).
  const payLink = useFieldPayLink(job.id);
  const amount = Math.round(Number(amountText) * 100) / 100;
  const amountOk = Number.isFinite(amount) && amount >= 0.5 && amount <= m.due + 0.005;
  const first = firstOf(job.customer_name);
  const title = view === "menu" ? "Collect the Balance"
    : view === "saved" ? "Card on File" : view === "card" ? "Type In a Card" : view === "check" ? "Check"
    : view === "cash" ? "Cash" : view === "klarna" ? "Klarna — Pay Over Time" : "Collected";
  const back = view === "menu" || view === "done" ? onClose : () => setView("menu");
  const finish = (d: { amount: number; how: string; cash?: boolean }) => { haptic("success"); setDone(d); setView("done"); };

  return (
    <Sheet title={title} sub={[job.customer_name, view !== "menu" && view !== "done" && amountOk ? money(amount) : null].filter(Boolean).join(" · ")} onClose={back}>
      {cfg?.test_mode && view !== "done" && (
        <p className="rounded-2xl bg-sky-50 px-3 py-2 text-center text-[12px] font-semibold text-sky-800">Test mode — no real money moves.</p>
      )}

      {view === "menu" && (
        <>
          <section className={cn(card, "text-center")}>
            {editAmount ? (
              <label className="block">
                <span className="text-[12px] font-bold text-slate-500">Amount to collect (up to {money(m.due)})</span>
                <input inputMode="decimal" autoFocus value={amountText}
                  onChange={(e) => setAmountText(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                  className={cn(input, "mt-1.5 text-center text-[26px] font-extrabold tabular-nums")} />
                {!amountOk && <span className="mt-1 block text-[12px] font-semibold text-rose-600">Between $0.50 and {money(m.due)}.</span>}
              </label>
            ) : (
              <>
                <div className="text-[38px] font-extrabold tabular-nums tracking-tight text-slate-900">{money(amount)}</div>
                <button type="button" onClick={() => setEditAmount(true)} className="mt-0.5 text-[13px] font-semibold text-sky-700">Change the amount</button>
              </>
            )}
          </section>
          <div className="px-1 pt-1 text-[12.5px] font-bold text-slate-500">Card</div>
          <Method icon={Nfc} title="Tap to Pay" sub="Card or phone taps this iPhone" disabled pill="App update"
            tip="Tap to Pay (iPhone) and the card reader (iPad) turn on with the next Pegasus app update." />
          <Method icon={Smartphone} title="Card Reader" sub="Tap, insert or swipe on the reader" disabled pill="App update"
            tip="Tap to Pay (iPhone) and the card reader (iPad) turn on with the next Pegasus app update." />
          {profile?.default_payment_method_id && (
            <Method icon={CreditCard} title="Card on File" sub={`${profile.card_brand ? profile.card_brand.charAt(0).toUpperCase() + profile.card_brand.slice(1) : "Card"} ending ${profile.card_last4 ?? "····"}`}
              disabled={!amountOk} onClick={() => setView("saved")} />
          )}
          <Method icon={CreditCard} title="Type In a Card" sub="Card number typed on this screen" disabled={!amountOk} onClick={() => setView("card")} />
          <div className="px-1 pt-2 text-[12.5px] font-bold text-slate-500">Other ways</div>
          <Method icon={FileText} title="Check" sub="Check number and a photo" disabled={!amountOk} onClick={() => setView("check")} />
          {cashCfg.enabled && <Method icon={Banknote} title="Cash" sub="The office is told right away" disabled={!amountOk} onClick={() => setView("cash")} />}
          <Method icon={QrCode} title="Klarna — Pay Over Time" sub={`${first} applies on their own phone`} onClick={() => { setView("klarna"); if (!payLink.data && !payLink.isPending) payLink.mutate(); }} />
        </>
      )}

      {view === "saved" && profile && (
        <SavedCardView job={job} amount={amount} brand={profile.card_brand} last4={profile.card_last4} first={first}
          onDone={() => finish({ amount, how: `${profile.card_brand ?? "Card"} •${profile.card_last4 ?? ""}` })} />
      )}
      {view === "card" && (
        <TypeCardView job={job} amount={amount} publishableKey={cfg?.publishable_key ?? null} first={first}
          onDone={(how) => finish({ amount, how })} />
      )}
      {view === "check" && <CheckView job={job} amount={amount} onDone={(no) => finish({ amount, how: `Check #${no}` })} />}
      {view === "cash" && <CashView job={job} amount={amount} onDone={() => finish({ amount, how: "Cash", cash: true })} />}
      {view === "klarna" && (
        <KlarnaView job={job} link={payLink.data ?? null} error={payLink.isError ? (payLink.error as Error).message : null}
          onRetry={() => payLink.mutate()} onPaid={(amt) => finish({ amount: amt, how: "Klarna" })} />
      )}

      {view === "done" && done && (
        <>
          <section className={cn(card, "flex flex-col items-center gap-1.5 py-6 text-center")}>
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><Check className="h-7 w-7" strokeWidth={3} /></span>
            <div className="text-[22px] font-extrabold tracking-tight text-slate-900">Collected {money(done.amount)}</div>
            <div className="text-[13px] text-slate-500">{done.how} · {clock(new Date().toISOString())}</div>
          </section>
          {done.cash && done.amount > cashCfg.turnInOver && (
            <p className="flex items-start gap-2 rounded-2xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold text-amber-900">
              <Banknote className="mt-0.5 h-4 w-4 shrink-0" /> Bring the cash to the office today — it&apos;s on your Today screen until the office marks it received.
            </p>
          )}
          <button type="button" onClick={onClose} className="m-btn m-btn-good m-btn-big w-full"><Check className="h-[18px] w-[18px]" /> Back to Wrap Up</button>
        </>
      )}
    </Sheet>
  );
}

function Method({ icon: Icon, title, sub, onClick, disabled, pill, tip }: {
  icon: typeof Wallet; title: string; sub: string; onClick?: () => void; disabled?: boolean; pill?: string; tip?: string;
}) {
  return (
    <div className={cn("flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-3 ring-1 ring-slate-200", disabled && "opacity-55")}>
      <button type="button" disabled={disabled} onClick={onClick} className="flex min-w-0 flex-1 items-center gap-3 text-left [touch-action:manipulation]">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700"><Icon className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-bold text-slate-900">{title}</span>
          <span className="block truncate text-[12px] text-slate-500">{sub}</span>
        </span>
        {pill ? <span className="m-chip shrink-0">{pill}</span> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />}
      </button>
      {tip && <InfoTip label={`About ${title}`} align="end">{tip}</InfoTip>}
    </div>
  );
}

function ErrorLine({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <p className="flex items-start gap-2 rounded-2xl bg-rose-50 px-3 py-2.5 text-[13px] font-semibold text-rose-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {msg}</p>;
}

function SavedCardView({ job, amount, brand, last4, first, onDone }: {
  job: Job; amount: number; brand: string | null; last4: string | null; first: string; onDone: () => void;
}) {
  const charge = useFieldChargeSaved(job.id);
  const [ok, setOk] = useState(false);
  const label = `${brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card"} ending ${last4 ?? "····"}`;
  return (
    <>
      <section className={cn(card, "text-center")}>
        <div className="text-[13px] font-semibold text-slate-500">Charge {label}</div>
        <div className="mt-1 text-[34px] font-extrabold tabular-nums tracking-tight text-slate-900">{money(amount)}</div>
      </section>
      <label className="flex items-center gap-3 rounded-2xl bg-white px-3.5 py-3 ring-1 ring-slate-200">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="h-5 w-5 accent-emerald-600" />
        <span className="text-[14px] font-semibold text-slate-800">{first} said OK to charge this card</span>
      </label>
      <ErrorLine msg={charge.isError ? (charge.error as Error).message : null} />
      <button type="button" disabled={!ok || charge.isPending} onClick={() => charge.mutate({ amount }, { onSuccess: (r) => (r?.ok !== false ? onDone() : undefined) })}
        className="m-btn m-btn-primary m-btn-big w-full">
        {charge.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <CreditCard className="h-[18px] w-[18px]" />} Charge {money(amount)}
      </button>
    </>
  );
}

function TypeCardView({ job, amount, publishableKey, first, onDone }: {
  job: Job; amount: number; publishableKey: string | null; first: string; onDone: (how: string) => void;
}) {
  const charge = useFieldChargeNewCard(job.id);
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeJs | null>(null);
  const cardRef = useRef<StripeCardElement | null>(null);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [save, setSave] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!publishableKey) return;
    void loadStripeJs(publishableKey).then((stripe) => {
      if (!alive || !stripe || !mountRef.current) return;
      stripeRef.current = stripe;
      const el = stripe.elements().create("card", {
        style: { base: { fontSize: "17px", color: "#0f172a", "::placeholder": { color: "#94a3b8" } } },
        hidePostalCode: false,
      });
      el.mount(mountRef.current);
      el.on("change", (e) => { setComplete(!!e.complete); setErr(e.error?.message ?? null); });
      el.on("ready", () => setReady(true));
      cardRef.current = el;
    });
    return () => { alive = false; try { cardRef.current?.unmount(); } catch { /* gone */ } };
  }, [publishableKey]);

  const go = async () => {
    const stripe = stripeRef.current;
    if (!stripe || !cardRef.current) return;
    setBusy(true);
    setErr(null);
    try {
      const pm = await stripe.createPaymentMethod({ type: "card", card: cardRef.current });
      if (pm.error || !pm.paymentMethod) throw new Error(pm.error?.message ?? "That card didn't work — check the number.");
      const r = await charge.mutateAsync({
        amount, payment_method_id: pm.paymentMethod.id, customer_name: job.customer_name ?? null, save_card: save,
        consent_text: save ? `${first} agreed to save this card for future charges (said in person to the installer).` : undefined,
      });
      if (r?.requires_action && r.client_secret && r.payment_id) {
        // The bank wants the customer to confirm (3-D Secure) — Stripe shows it here.
        const next = await stripe.handleNextAction({ clientSecret: r.client_secret });
        if (next.error) throw new Error(next.error.message ?? "The bank didn't confirm the card.");
        const s = await settleFieldPayment(r.payment_id);
        if (!s?.ok) throw new Error(s?.error ?? "The card didn't go through.");
      }
      onDone("Card");
    } catch (e) {
      setErr((e as Error).message ?? "The card didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  if (!publishableKey) return <ErrorLine msg="Card payments aren't set up yet — use another way or call the office." />;
  return (
    <>
      <section className={card}>
        <div className="text-[12.5px] font-bold text-slate-500">Card number</div>
        <div ref={mountRef} className="mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3.5" />
        {!ready && <p className="mt-2 text-[12px] text-slate-400">Loading secure card fields…</p>}
      </section>
      <label className="flex items-center gap-3 rounded-2xl bg-white px-3.5 py-3 ring-1 ring-slate-200">
        <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="h-5 w-5 accent-emerald-600" />
        <span className="text-[14px] font-semibold text-slate-800">{first} wants this card saved for next time</span>
      </label>
      <ErrorLine msg={err} />
      <button type="button" disabled={!complete || busy} onClick={() => void go()} className="m-btn m-btn-primary m-btn-big w-full">
        {busy ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <CreditCard className="h-[18px] w-[18px]" />} Charge {money(amount)}
      </button>
    </>
  );
}

function CheckView({ job, amount, onDone }: { job: Job; amount: number; onDone: (no: string) => void }) {
  const rec = useFieldRecord(job.id);
  const qc = useQueryClient();
  const [no, setNo] = useState("");
  const [photo, setPhoto] = useState<{ id: string; url: string } | null>(null);
  const [upBusy, setUpBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const onPhoto = async (f: File | undefined) => {
    if (!f) return;
    setUpBusy(true);
    setErr(null);
    try {
      const row = await uploadOneFile({ custno: job.custno, locno: job.locno, job_id: job.id, caption: `Check${no.trim() ? ` #${no.trim()}` : ""}`, doc_tag: "photo", origin: "tech" }, f);
      setPhoto({ id: row.id, url: URL.createObjectURL(f) });
      void qc.invalidateQueries({ queryKey: ["files"] });
    } catch {
      setErr("The photo didn't upload — check the signal and try again.");
    } finally {
      setUpBusy(false);
    }
  };
  return (
    <>
      <section className={card}>
        <label className="block">
          <span className="text-[12.5px] font-bold text-slate-500">Check number</span>
          <input value={no} onChange={(e) => setNo(e.target.value.replace(/[^0-9A-Za-z-]/g, ""))} inputMode="numeric" autoFocus className={cn(input, "mt-1.5 font-mono text-[18px] font-bold")} />
        </label>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void onPhoto(e.target.files?.[0])} />
        <button type="button" disabled={upBusy} onClick={() => fileRef.current?.click()} className={cn("m-btn mt-3 w-full", photo ? "m-btn-soft" : "m-btn-tint")}>
          {upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : photo ? <Check className="h-4 w-4 text-emerald-600" /> : <Camera className="h-4 w-4" />}
          {photo ? "Retake the Check Photo" : "Photograph the Check"}
        </button>
        {photo && <img src={photo.url} alt="The check" className="mt-2 max-h-40 w-full rounded-xl object-contain" />}
      </section>
      <ErrorLine msg={err ?? (rec.isError ? (rec.error as Error).message : null)} />
      <button type="button" disabled={!no.trim() || rec.isPending || upBusy}
        onClick={() => rec.mutate({ amount, method: "check", reference: no.trim(), check_file_id: photo?.id ?? null }, { onSuccess: () => onDone(no.trim()) })}
        className="m-btn m-btn-primary m-btn-big w-full">
        {rec.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Record {money(amount)} Check
      </button>
    </>
  );
}

function CashView({ job, amount, onDone }: { job: Job; amount: number; onDone: () => void }) {
  const rec = useFieldRecord(job.id);
  const [recvText, setRecvText] = useState(amount.toFixed(2));
  const recv = Math.round(Number(recvText) * 100) / 100;
  const change = Math.round((recv - amount) * 100) / 100;
  const ok = Number.isFinite(recv) && recv + 0.005 >= amount;
  return (
    <>
      <section className={card}>
        <label className="block">
          <span className="text-[12.5px] font-bold text-slate-500">Cash received</span>
          <input inputMode="decimal" value={recvText} onChange={(e) => setRecvText(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
            className={cn(input, "mt-1.5 text-[22px] font-extrabold tabular-nums")} />
        </label>
        <div className="mt-2.5 flex items-baseline justify-between text-[14px]">
          <span className="text-slate-500">Change back</span>
          <span className="font-bold tabular-nums text-slate-900">{ok ? money(Math.max(0, change)) : "—"}</span>
        </div>
        {!ok && <p className="mt-1 text-[12px] font-semibold text-rose-600">That&apos;s less than {money(amount)}.</p>}
      </section>
      <ErrorLine msg={rec.isError ? (rec.error as Error).message : null} />
      <button type="button" disabled={!ok || rec.isPending}
        onClick={() => rec.mutate({ amount, method: "cash", note: change > 0.005 ? `Received ${money(recv)}, gave ${money(change)} back` : null }, { onSuccess: onDone })}
        className="m-btn m-btn-primary m-btn-big w-full">
        {rec.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Banknote className="h-[18px] w-[18px]" />} Record {money(amount)} Cash
      </button>
    </>
  );
}

function KlarnaView({ job, link, error, onRetry, onPaid }: {
  job: Job; link: PayLink | null; error: string | null; onRetry: () => void; onPaid: (amount: number) => void;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const { data: pays } = useJobPayments(job.id);
  // The customer pays on their phone — check every few seconds.
  useEffect(() => {
    if (!link) return;
    const t = window.setInterval(() => {
      void qc.invalidateQueries({ queryKey: ["job_payments", job.id] });
      void qc.invalidateQueries({ queryKey: ["job_money", job.id] });
    }, 4000);
    return () => window.clearInterval(t);
  }, [link, job.id, qc]);
  const paid = link && (pays ?? []).find((p) => p.method === "klarna" && (p.status === "succeeded" || p.status === "processing") && p.created_at >= new Date(Date.parse(link.expires_at) - 24 * 3600 * 1000 - 60000).toISOString());
  useEffect(() => { if (paid) onPaid(paid.amount); }, [paid, onPaid]);

  if (error) {
    return (
      <>
        <ErrorLine msg={error} />
        <button type="button" onClick={onRetry} className="m-btn m-btn-soft w-full">Try Again</button>
      </>
    );
  }
  if (!link) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <>
      <section className={cn(card, "flex flex-col items-center gap-3 py-5 text-center")}>
        <div className="text-[18px] font-extrabold tracking-tight text-slate-900">{firstOf(job.customer_name)} scans this with their camera</div>
        <div className="h-[240px] w-[240px] [&>svg]:h-full [&>svg]:w-full" role="img" aria-label="QR code to pay with Klarna" dangerouslySetInnerHTML={{ __html: link.svg }} />
        <div className="flex items-center gap-1.5 text-[13px] text-slate-500">
          {money(link.amount)} balance
          <InfoTip label="What the page shows" align="end">The page shows only this install&apos;s balance — nothing else on their account. It stops working after 24 hours.</InfoTip>
        </div>
      </section>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => { void navigator.clipboard?.writeText(link.url); setCopied(true); haptic("light"); }} className="m-btn m-btn-soft">
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy Link"}
        </button>
        <a href={`/pay/${link.token}`} target="_blank" rel="noreferrer" className="m-btn m-btn-soft"><ExternalLink className="h-4 w-4" /> Open the Page</a>
      </div>
      <p className="flex items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold text-amber-900">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> Waiting for Klarna to approve {firstOf(job.customer_name)}…
      </p>
    </>
  );
}

/* ------------------------------------------------- Today: cash to turn in */

/** On the installer's Today: cash over the Settings amount goes back to the
 *  office the same day. Clears when the office marks it received. */
export function CashTurnInCards({ empno }: { empno: string | null }) {
  const { data } = useFieldCash(empno);
  const cfg = useFieldCashSettings();
  const drop = useCashDroppedOff();
  const rows = (data ?? []).filter((c) => Number(c.amount) > cfg.turnInOver);
  if (!empno || rows.length === 0) return null;
  return (
    <>
      {rows.map((c) => (
        <section key={c.payment_id} className={cn(card, "border-2 !border-amber-300")}>
          <div className="flex items-center gap-2">
            <Banknote className="h-[18px] w-[18px] text-amber-600" />
            <h4 className="text-[15px] font-extrabold tracking-tight text-amber-800">{c.cash_dropped_at ? "Cash dropped off" : "Bring cash to the office today"}</h4>
            <InfoTip label="Why cash goes back today" align="end">Cash over {money(cfg.turnInOver)} goes back to the office the same day. The office marks it received and this card goes away.</InfoTip>
          </div>
          <p className="mt-1.5 text-[14px] font-semibold text-slate-800">{money(Number(c.amount))} · {c.job_title ?? "Install"}</p>
          {c.cash_dropped_at ? (
            <p className="mt-1 text-[12.5px] text-slate-500">You dropped it off at {clock(c.cash_dropped_at)} — waiting for the office to mark it received.</p>
          ) : (
            <div className={cn("mt-3 grid gap-2", cfg.officeAddress ? "grid-cols-2" : "grid-cols-1")}>
              {cfg.officeAddress && (
                <a href={`https://maps.apple.com/?daddr=${encodeURIComponent(cfg.officeAddress)}`} className="m-btn m-btn-soft"><MapPin className="h-4 w-4" /> Directions</a>
              )}
              <button type="button" disabled={drop.isPending} onClick={() => drop.mutate({ paymentId: c.payment_id })} className="m-btn m-btn-tint">
                {drop.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} I Dropped It Off
              </button>
            </div>
          )}
        </section>
      ))}
    </>
  );
}
